import {
  Modal,
  Box,
  Text,
  Group,
  Stack,
  ThemeIcon,
  useMantineTheme,
} from '@mantine/core';
import { forwardRef } from 'react';
import {
  IconAlertTriangle,
  IconTrash,
  IconInfoCircle,
  IconCheck,
  IconQuestionMark,
} from '@tabler/icons-react';
import { Button } from '@/components/ui';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface ConfirmDialogProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title?: string;
  message?: string | React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  type?: 'danger' | 'warning' | 'info' | 'success' | 'question';
  loading?: boolean;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  centered?: boolean;
  closeOnConfirm?: boolean;
  closeOnCancel?: boolean;
}

const getTypeConfig = (type: ConfirmDialogProps['type']) => {
  switch (type) {
    case 'danger':
      return {
        icon: <IconTrash size={24} />,
        color: 'red',
        confirmColor: 'red',
      };
    case 'warning':
      return {
        icon: <IconAlertTriangle size={24} />,
        color: 'yellow',
        confirmColor: 'yellow',
      };
    case 'success':
      return {
        icon: <IconCheck size={24} />,
        color: 'teal',
        confirmColor: 'teal',
      };
    case 'info':
      return {
        icon: <IconInfoCircle size={24} />,
        color: 'blue',
        confirmColor: 'blue',
      };
    case 'question':
    default:
      return {
        icon: <IconQuestionMark size={24} />,
        color: 'gray',
        confirmColor: undefined,
      };
  }
};

const ConfirmDialog = forwardRef<HTMLDivElement, ConfirmDialogProps>(
  (
    {
      opened,
      onClose,
      onConfirm,
      title = 'Confirm Action',
      message = 'Are you sure you want to proceed?',
      confirmLabel = 'Confirm',
      cancelLabel = 'Cancel',
      type = 'question',
      loading = false,
      icon,
      children,
      gradientVariant = 'primary',
      size = 'sm',
      centered = true,
      closeOnConfirm = true,
      closeOnCancel = true,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other?.gradients?.[gradientVariant];
    const typeConfig = getTypeConfig(type);
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const handleConfirm = async () => {
      await onConfirm();
      if (closeOnConfirm) {
        onClose();
      }
    };

    const handleCancel = () => {
      if (closeOnCancel) {
        onClose();
      }
    };

    return (
      <Modal
        opened={opened}
        onClose={onClose}
        withCloseButton={false}
        size={size}
        centered={centered}
        padding="lg"
      >
        <Box ref={ref}>
          <Stack align="center" gap="md">
            {/* Icon */}
            <ThemeIcon
              size={60}
              radius="xl"
              variant="light"
              color={typeConfig.color}
            >
              {icon || typeConfig.icon}
            </ThemeIcon>

            {/* Title */}
            <Text fw={600} size="lg" ta="center" style={accessibilityStyles}>
              {title}
            </Text>

            {/* Message */}
            {typeof message === 'string' ? (
              <Text size="sm" c="dimmed" ta="center" style={accessibilityStyles}>
                {message}
              </Text>
            ) : (
              message
            )}

            {/* Custom content */}
            {children}
          </Stack>

          {/* Actions */}
          <Group justify="center" mt="xl">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={loading}
            >
              {cancelLabel}
            </Button>
            <Button
              color={typeConfig.confirmColor}
              onClick={handleConfirm}
              loading={loading}
              gradientVariant={type === 'question' ? gradientVariant : undefined}
            >
              {confirmLabel}
            </Button>
          </Group>
        </Box>
      </Modal>
    );
  }
);

ConfirmDialog.displayName = 'ConfirmDialog';

export default ConfirmDialog;
